/**
 * Guandan Agent Communication Protocol (GACP v1.0)
 * Open specification implementation for AI Agent & API Player integration.
 */
import { Card, Combo, GameState, LevelRank, PlayerSeat } from './types';
import { isWildcard } from './cards';
import { legalPlays } from './moves';
import { describeCombo } from './combos';

export interface GACPCard {
  id: string;
  suit: 'S' | 'H' | 'C' | 'D';
  rank: string;
  isWildcard: boolean;
}

export interface GACPComboInfo {
  category: string;
  length: number;
  compareValue: number;
  isBomb: boolean;
  name: string;
}

export interface GACPLegalMove {
  action: 'play' | 'pass';
  cardIds?: string[];
  comboCategory?: string;
  name: string;
}

export interface GACPObservation {
  matchId: string;
  levelRank: string;
  seat: PlayerSeat;
  isMyTurn: boolean;
  phase: string;
  timeoutMs: number;
  myHand: GACPCard[];
  table: {
    mustBeatCombo: GACPComboInfo | null;
    trickHistory: Array<{ seat: PlayerSeat; action: 'play' | 'pass'; comboName?: string }>;
  };
  publicInfo: {
    remainingCounts: Record<PlayerSeat, number>;
    finishedOrder: PlayerSeat[];
  };
  legalMoves: GACPLegalMove[];
}

/**
 * Extract a strictly fair, non-cheating Observation JSON for a specific seat.
 * Guarantees that the Agent CANNOT see opponents' or partner's hidden cards.
 */
export function extractObservation(
  gameState: GameState,
  seat: PlayerSeat,
  timeoutMs: number = 15000
): GACPObservation {
  const { currentTurn, hands, levelRank, phase, currentCombo, trickPlays, finishedOrder, mode } = gameState;
  const is6p = mode === '6p' || hands.length === 6;
  const isMyTurn = currentTurn === seat && phase === 'playing';

  // 1. Agent's own hand (strictly isolated)
  const myCards = hands[seat] || [];
  const formattedHand: GACPCard[] = myCards.map((c) => ({
    id: c.id,
    suit: c.suit,
    rank: c.rank,
    isWildcard: isWildcard(c, levelRank),
  }));

  // 2. Table Combo to beat
  const formattedCombo: GACPComboInfo | null = currentCombo
    ? {
        category: currentCombo.category,
        length: currentCombo.length,
        compareValue: currentCombo.compareValue,
        isBomb: currentCombo.isBomb,
        name: describeCombo(currentCombo),
      }
    : null;

  // 3. Trick History of current round
  const trickHistory = Object.entries(trickPlays)
    .filter(([_, play]) => play !== null)
    .map(([sStr, play]) => ({
      seat: Number(sStr) as PlayerSeat,
      action: play!.action,
      comboName: play!.combo ? describeCombo(play!.combo) : undefined,
    }));

  // 4. Public remaining card counts
  const remainingCounts: Record<PlayerSeat, number> = {} as any;
  const totalSeats = is6p ? 6 : 4;
  for (let s = 0; s < totalSeats; s++) {
    const count = hands[s]?.length || 0;
    remainingCounts[s as PlayerSeat] = count;
  }

  // 5. Pre-computed legal moves (Zero hallucination guardrail)
  const legalMoves: GACPLegalMove[] = [];
  if (isMyTurn) {
    if (currentCombo !== null) {
      legalMoves.push({ action: 'pass', name: '过牌 (Pass)' });
    }
    const candidateCombos = legalPlays(myCards, currentCombo, levelRank);
    for (const combo of candidateCombos) {
      legalMoves.push({
        action: 'play',
        cardIds: combo.cards.map((c) => c.id),
        comboCategory: combo.category,
        name: describeCombo(combo),
      });
    }
  }

  return {
    matchId: `match_${Date.now()}`,
    levelRank,
    seat,
    isMyTurn,
    phase,
    timeoutMs,
    myHand: formattedHand,
    table: {
      mustBeatCombo: formattedCombo,
      trickHistory,
    },
    publicInfo: {
      remainingCounts,
      finishedOrder: finishedOrder || [],
    },
    legalMoves,
  };
}
